import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebounceCallback } from 'usehooks-ts'

import {
  getAmountStep,
  getMaxAmount,
  getMinAmount,
  normalizeAmount,
} from '@entities/Product/helpers'

import { useAuth } from '@features/auth'
import useCartItemTotal from '@features/cart/hooks/useCartItemTotal'
import { useCartStore } from '@features/cart/model/useCartStore'
import { useErrorInfoModal } from '@features/modal'


import { DEBOUNCE_TIMEOUT_UPDATE_AMOUNT } from '@shared/contants/product'
import {
  Product,
  useDropCartItemMutation,
  useUpdateCartProductAmountMutation,
} from '@shared/generated'
import { useProductUnit } from '@shared/hooks'
import { cn } from '@shared/lib/cn'
import { formatPrice } from '@shared/uttils'


import styles from './styles.module.scss'

interface ProductCounterProps {
  shopId: string;
  className?: string;
  size?: 'small';
  product: Product;
  invertInfo?: boolean;
}

export default function ProductCounter (props: ProductCounterProps) {
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const { className, size, product, invertInfo } = props
  const { getProduct, addOrUpdateProduct, removeProduct, currentShopId } =
    useCartStore()
  const cartItem = getProduct(product.id)
  const { isAuthorized } = useAuth()
  const [ fetchUpdateCart ] = useUpdateCartProductAmountMutation()
  const [ fetchRemoveProduct ] = useDropCartItemMutation()

  const amount = useMemo(() => cartItem?.amount || 0, [ cartItem?.amount ])
  const step = getAmountStep(product) as number
  const { maxAmount } = getMaxAmount(product)
  const debouncedUpdate = useDebounceCallback(
    fetchUpdateCart,
    DEBOUNCE_TIMEOUT_UPDATE_AMOUNT,
  )
  const cartItemTotalPrice = useCartItemTotal(product, amount)

  const handleIncrement = async () => {
    const newAmount = normalizeAmount(amount + step, step)

    if (newAmount <= maxAmount) {
      if (isAuthorized) {
        try {
          await debouncedUpdate({
            variables: {
              shopId: currentShopId as string,
              itemData: {
                amount: newAmount,
                product_id: product.id,
              },
            },
          })

          addOrUpdateProduct({
            product_id: product.id,
            amount: newAmount,
          })
        }
        catch (error) {
          console.error('fetchUpdateCart error: ', error)
          openErrorInfoModal({
            description: te('update_cart_item'),
          })
        }
      }
      else {
        addOrUpdateProduct({
          product_id: product.id,
          amount: newAmount,
        })
      }
    }
  }

  const handleDecrement = async () => {
    const newAmount = normalizeAmount(amount - step, step)
    const min = getMinAmount(product)

    if (newAmount >= min) {
      if (isAuthorized) {
        try {
          await debouncedUpdate({
            variables: {
              shopId: currentShopId as string,
              itemData: {
                amount: newAmount,
                product_id: product.id,
              },
            },
          })

          addOrUpdateProduct({
            product_id: product.id,
            amount: newAmount,
          })
        }
        catch (error) {
          console.error('fetchUpdateCart error: ', error)
          openErrorInfoModal({
            description: te('update_cart_item'),
          })
        }
      }
      else {
        addOrUpdateProduct({
          product_id: product.id,
          amount: newAmount,
        })
      }
    }
    else {
      if (isAuthorized) {
        try {
          await fetchRemoveProduct({
            variables: {
              shopId: currentShopId as string,
              productId: product.id,
            },
          })

          removeProduct(product.id)
        }
        catch (error) {
          console.error('fetchRemoveProduct error: ', error)
          openErrorInfoModal({
            description: te('drop_cart_item'),
          })
        }
      }
      else {
        removeProduct(product.id)
      }
    }
  }

  const { unit, value } = useProductUnit({
    isWeightProduct: product.isWeightProduct as boolean,
    value: amount,
    price: cartItemTotalPrice as number,
    t: tc,
  })

  return (
    <div
      className={cn(
        styles.root,
        className,
        size ? styles[size] : null,
        invertInfo ? 'product_counter_tabbar' : null,
      )}
    >
      <button
        className={cn(
          styles['root__button'],
          'reset-button',
          styles.root__dec,
          {
            [styles.disabled]: amount <= 0,
          },
        )}
        type="button"
        onClick={handleDecrement}
        disabled={amount <= 0}
      ></button>
      <div className={styles.root__info}>
        {invertInfo ? (
          <>
            <b>
              {value} {unit}
            </b>
            {!!cartItemTotalPrice && (
              <span>{formatPrice(cartItemTotalPrice)}</span>
            )}
          </>
        ) : (
          <>
            {!!cartItemTotalPrice && <b>{formatPrice(cartItemTotalPrice)}</b>}
            <span>
              {value} {unit}
            </span>
          </>
        )}
      </div>
      <button
        className={cn(
          styles['root__button'],
          'reset-button',
          styles.root__inc,
          {
            [styles.disabled]: amount >= maxAmount,
          },
        )}
        type="button"
        onClick={handleIncrement}
        disabled={amount >= maxAmount}
      ></button>
    </div>
  )
}
